/**
 * DriverManagementPage — `/delivery/drivers`.
 *
 * Live · GET /v1/drivers, POST /v1/drivers, POST /v1/drivers/:id/status,
 * POST /v1/drivers/:id/vehicle.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, RefreshCw, Truck } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  assignVehicleToDriver,
  createDriver,
  type Driver,
  type DriverStatus,
  listDrivers,
  listVehicles,
  updateDriverStatus,
} from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';

const STATUS_OPTIONS: readonly (DriverStatus | '')[] = ['', 'ACTIVE', 'SUSPENDED', 'TERMINATED'];

export function DriverManagementPage(): JSX.Element {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<DriverStatus | ''>('');
  const [search, setSearch] = useState<string>('');
  const [showNew, setShowNew] = useState<boolean>(false);
  const [selected, setSelected] = useState<Driver | null>(null);

  const drivers = useQuery({
    queryKey: ['delivery', 'drivers', 'mgmt', { statusFilter }],
    queryFn: () => listDrivers({ pageSize: 50, ...(statusFilter !== '' && { status: statusFilter }) }),
    retry: false,
    staleTime: 15_000,
  });
  const vehicles = useQuery({
    queryKey: ['delivery', 'vehicles', 'all'],
    queryFn: () => listVehicles({ pageSize: 50 }),
    retry: false,
    staleTime: 30_000,
  });

  const denied = drivers.isError && drivers.error instanceof ApiError && (drivers.error.status === 401 || drivers.error.status === 403);

  const filtered = (drivers.data?.items ?? []).filter((d) => {
    if (search.trim() === '') return true;
    const needle = search.trim().toLowerCase();
    return d.fullName.toLowerCase().includes(needle) || d.driverNumber.toLowerCase().includes(needle) || d.primaryPhone.includes(needle);
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Driver Management"
        description="Operator-scoped driver roster."
        actions={
          <button
            type="button"
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            <Plus className="w-4 h-4" /> New driver
          </button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name, number, phone…"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DriverStatus | '')}
          aria-label="Status filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'all'} value={s}>{s === '' ? 'All statuses' : s}</option>
          ))}
        </select>
      </FilterBar>

      {drivers.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/readDriver</code>.
        </DeferredNotice>
      ) : drivers.isError ? (
        <DeferredNotice title="Service unreachable">{(drivers.error as Error).message}</DeferredNotice>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
              <tr>
                <th className="text-left px-4 py-2">Driver</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2">Licence</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Vehicle</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-xs">{d.fullName} <span className="text-muted ml-1">{d.driverNumber}</span></td>
                  <td className="px-4 py-2 text-xs text-muted">{d.primaryPhone}</td>
                  <td className="px-4 py-2 text-xs text-muted">{d.licenseNumber ?? '—'}</td>
                  <td className="px-4 py-2"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">{d.assignedVehicleId ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button type="button" onClick={() => setSelected(d)} className="text-xs font-semibold text-nipost-green hover:underline">Manage →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 ? <p className="p-8 text-center text-sm text-muted">No drivers match.</p> : null}
        </div>
      )}

      {showNew ? (
        <NewDriverModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            void qc.invalidateQueries({ queryKey: ['delivery', 'drivers'] });
          }}
        />
      ) : null}
      {selected ? (
        <ManageDriverModal
          driver={selected}
          vehicleOptions={vehicles.data?.items ?? []}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            void qc.invalidateQueries({ queryKey: ['delivery', 'drivers'] });
          }}
        />
      ) : null}
    </div>
  );
}

function NewDriverModal(props: { readonly onClose: () => void; readonly onCreated: () => void }): JSX.Element {
  const [fullName, setFullName] = useState<string>('');
  const [primaryPhone, setPrimaryPhone] = useState<string>('');
  const [licenseNumber, setLicenseNumber] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => createDriver({ fullName, primaryPhone, ...(licenseNumber !== '' && { licenseNumber }) }),
    onSuccess: props.onCreated,
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
      <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">New driver</h3>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Full name</span>
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} required className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Phone</span>
          <input value={primaryPhone} onChange={(e) => setPrimaryPhone(e.target.value)} required className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-muted mb-1">Licence number (optional)</span>
          <input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className="w-full px-3 py-2 text-sm border border-border rounded-lg" />
        </label>
        {error ? <p className="text-xs text-red-700">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-2 text-sm border border-border rounded-lg">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg disabled:opacity-50">
            {mutation.isPending ? 'Creating…' : 'Create driver'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ManageDriverModal(props: {
  readonly driver: Driver;
  readonly vehicleOptions: readonly { id: string; registration: string }[];
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): JSX.Element {
  const [status, setStatus] = useState<DriverStatus>(props.driver.status);
  const [vehicleId, setVehicleId] = useState<string>(props.driver.assignedVehicleId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'status' | 'vehicle' | null>(null);

  const statusMutation = useMutation({
    mutationFn: () => updateDriverStatus(props.driver.id, status),
    onMutate: () => setBusy('status'),
    onSuccess: props.onSaved,
    onError: (err) => {
      setBusy(null);
      setError(err instanceof Error ? err.message : 'Status update failed');
    },
  });
  const vehicleMutation = useMutation({
    mutationFn: () => assignVehicleToDriver(props.driver.id, vehicleId),
    onMutate: () => setBusy('vehicle'),
    onSuccess: props.onSaved,
    onError: (err) => {
      setBusy(null);
      setError(err instanceof Error ? err.message : 'Vehicle assignment failed');
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{props.driver.fullName}</h3>
          <p className="text-xs text-muted">{props.driver.driverNumber} · {props.driver.primaryPhone}</p>
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-medium text-muted">Status</label>
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value as DriverStatus)} className="flex-1 px-3 py-2 text-sm border border-border rounded-lg">
              <option value="ACTIVE">ACTIVE</option>
              <option value="SUSPENDED">SUSPENDED</option>
              <option value="TERMINATED">TERMINATED</option>
            </select>
            <button
              type="button"
              onClick={() => statusMutation.mutate()}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 px-3 py-2 bg-nipost-green text-white text-xs font-semibold rounded-lg disabled:opacity-50"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Update
            </button>
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-xs font-medium text-muted">Assigned vehicle</label>
          <div className="flex items-center gap-2">
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="flex-1 px-3 py-2 text-sm border border-border rounded-lg">
              <option value="">— none —</option>
              {props.vehicleOptions.map((v) => (
                <option key={v.id} value={v.id}>{v.registration}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => vehicleMutation.mutate()}
              disabled={busy !== null || vehicleId === ''}
              className="inline-flex items-center gap-1 px-3 py-2 bg-nipost-green text-white text-xs font-semibold rounded-lg disabled:opacity-50"
            >
              <Truck className="w-3.5 h-3.5" /> Assign
            </button>
          </div>
        </div>
        {error ? <p className="text-xs text-red-700">{error}</p> : null}
        <div className="flex justify-end">
          <button type="button" onClick={props.onClose} className="px-3 py-2 text-sm border border-border rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}
